import type {
  RTPIds,
  RTPAggregatedResponse,
  RTPBucketRow,
  RTPColumnarSideResult,
  RTPGrouping,
  RTPDownloadResult,
  PatientRTPDownloadRequest,
  DoctorRTPDownloadRequest,
} from '@/types/rtpDownload';
import { api } from './client';
import { getCurrentSelfServiceBasePath } from './selfServiceBasePath';

// ============================================================================
// Patient Endpoints
// ============================================================================

/**
 * Get current patient's RTP IDs
 */
export async function getPatientRTPIds(): Promise<RTPIds> {
  return api.get<RTPIds>(`${getCurrentSelfServiceBasePath()}/rtps/ids/`);
}

/**
 * Get RTP data for current patient
 */
export async function getPatientRTPData(
  rtpIds: RTPIds,
  request: PatientRTPDownloadRequest
): Promise<RTPAggregatedResponse[]> {
  const fromTs = Math.floor(new Date(request.timeRange.from).getTime() / 1000);
  const toTs = Math.floor(new Date(request.timeRange.to).getTime() / 1000);

  return api.post<RTPAggregatedResponse[]>(
    `${getCurrentSelfServiceBasePath()}/rtps/`,
    rtpIds,
    {
      params: {
        from_ts: fromTs,
        to_ts: toTs,
        grouping: request.grouping,
      },
    }
  );
}

function toIsoFromUnixTs(timestamp: number | null | undefined): string {
  if (typeof timestamp !== 'number') {
    return new Date().toISOString();
  }
  return new Date(timestamp * 1000).toISOString();
}

function getNumericValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function formatBucketLabel(timestamp: number, grouping: RTPGrouping): string {
  const date = new Date(timestamp * 1000);

  switch (grouping) {
    case 'hour':
      return date.toISOString().slice(0, 13).replace('T', ' ') + ':00';
    case 'day':
      return date.toISOString().split('T')[0]!;
    case 'week': {
      const weekStart = new Date(date);
      const dayOfWeek = weekStart.getDay();
      const daysSinceMonday = (dayOfWeek + 6) % 7;
      weekStart.setDate(weekStart.getDate() - daysSinceMonday);
      return weekStart.toISOString().split('T')[0]!;
    }
    case 'month':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }
}

function resolveColumnIndex(columns: string[], aliases: string[]): number {
  return columns.findIndex((column) => aliases.includes(column));
}

function normalizeColumnarSideResult(
  side: RTPColumnarSideResult,
  grouping: RTPGrouping
): RTPBucketRow[] {
  const timestampIndex = resolveColumnIndex(side.columns, ['time', 'timestamp', 't']);
  if (timestampIndex < 0) return [];

  const countIndex = resolveColumnIndex(side.columns, ['count']);
  const durationIndex = resolveColumnIndex(side.columns, ['avg_duration', 'duration-avg']);
  const lengthIndex = resolveColumnIndex(side.columns, ['avg_length', 'length-avg']);

  return side.rows
    .map((row) => {
      const timestamp = getNumericValue(row[timestampIndex]);
      if (timestamp === undefined) return null;

      const normalized: RTPBucketRow = {
        timestamp,
        bucketLabel: formatBucketLabel(timestamp, grouping),
      };

      const count = countIndex >= 0 ? getNumericValue(row[countIndex]) : undefined;
      const durationAvg = durationIndex >= 0 ? getNumericValue(row[durationIndex]) : undefined;
      const lengthAvg = lengthIndex >= 0 ? getNumericValue(row[lengthIndex]) : undefined;

      if (count !== undefined) normalized.count = count;
      if (durationAvg !== undefined) normalized['duration-avg'] = durationAvg;
      if (lengthAvg !== undefined) normalized['length-avg'] = lengthAvg;

      return normalized;
    })
    .filter((bucket): bucket is RTPBucketRow => bucket !== null);
}

function toDownloadResult(
  aggregated: RTPAggregatedResponse,
  patientId: string,
  patientName?: string
): RTPDownloadResult {
  const leftFoot = normalizeColumnarSideResult(aggregated.results.left, aggregated.grouping);
  const rightFoot = normalizeColumnarSideResult(aggregated.results.right, aggregated.grouping);

  return {
    patientId,
    patientName,
    timeRange: {
      from: toIsoFromUnixTs(aggregated.timeRange?.from),
      to: toIsoFromUnixTs(aggregated.timeRange?.to),
    },
    grouping: aggregated.grouping,
    leftFoot,
    rightFoot,
  };
}

/**
 * Orchestrated download function for patients
 * Fetches IDs, data, and aggregates
 */
export async function downloadPatientRTP(
  request: PatientRTPDownloadRequest,
  patientId: string,
  patientName?: string
): Promise<RTPDownloadResult> {
  // Step 1: Fetch RTP IDs
  const rtpIds = await getPatientRTPIds();

  // Step 2: Fetch RTP data
  const aggregatedResponses = await getPatientRTPData(rtpIds, request);
  const ownResponse =
    aggregatedResponses.find((item) => item.username === patientId) ||
    aggregatedResponses[0];

  if (!ownResponse) {
    return {
      patientId,
      patientName,
      timeRange: request.timeRange,
      grouping: request.grouping,
      leftFoot: [],
      rightFoot: [],
    };
  }

  return toDownloadResult(ownResponse, patientId, patientName);
}

// ============================================================================
// Doctor Endpoints
// ============================================================================

/**
 * Download RTP data for multiple patients using batch endpoint
 */
export async function downloadDoctorRTP(
  request: DoctorRTPDownloadRequest,
  patients: Array<{ id: string; name: string; token: string }>
): Promise<{
  successful: RTPDownloadResult[];
  failed: Array<{ patientId: string; patientName: string; error: string }>;
}> {
  // Real API implementation
  try {
    // Build UserAccess array
    const userAccesses = patients.map((patient) => ({
      token: patient.token,
      user_id: patient.id,
    }));

    // Make single batch request
    // Add from_date and to_date as query parameters
    const fromDate = new Date(request.timeRange.from).toISOString();
    const toDate = new Date(request.timeRange.to).toISOString();
    const fromTs = Math.floor(new Date(fromDate).getTime() / 1000);
    const toTs = Math.floor(new Date(toDate).getTime() / 1000);

    const responses = await api.post<RTPAggregatedResponse[]>(
      '/doctor/patients/rtps/',
      userAccesses,
      {
        params: {
          from_ts: fromTs,
          to_ts: toTs,
          grouping: request.grouping,
        },
      }
    );

    const successful: RTPDownloadResult[] = [];
    const failed: Array<{ patientId: string; patientName: string; error: string }> = [];
    const patientById = new Map(patients.map((patient) => [patient.id, patient]));
    const seenPatientIds = new Set<string>();

    // Process each response
    responses.forEach((aggregatedResponse) => {
      const patient = patientById.get(aggregatedResponse.username);
      const patientId = patient?.id ?? aggregatedResponse.username;
      const patientName = patient?.name ?? aggregatedResponse.username;
      seenPatientIds.add(patientId);

      try {
        const result = toDownloadResult(aggregatedResponse, patientId, patientName);
        if (result.leftFoot.length === 0 && result.rightFoot.length === 0) {
          failed.push({
            patientId,
            patientName,
            error: 'No RTP data available',
          });
          return;
        }
        successful.push(result);
      } catch (error) {
        failed.push({
          patientId,
          patientName,
          error: error instanceof Error ? error.message : 'Processing failed',
        });
      }
    });

    patients.forEach((patient) => {
      if (!seenPatientIds.has(patient.id)) {
        failed.push({
          patientId: patient.id,
          patientName: patient.name,
          error: 'No RTP response returned',
        });
      }
    });

    return { successful, failed };
  } catch (error) {
    // If the entire request fails, mark all patients as failed
    const failed = patients.map((patient) => ({
      patientId: patient.id,
      patientName: patient.name,
      error: error instanceof Error ? error.message : 'Request failed',
    }));

    return { successful: [], failed };
  }
}
